import EnrolmentDetailsCard from '@components/Activation/EnrolmentDetailsCard';
import FileUpload from '@components/Shared/FileUpload';
import Header from '@components/Shared/Layout/Header';
import { expiresInText, expiryReminderSeverity } from '@constants/expiry';
import UploadIcon from '@mui/icons-material/UploadFile';
import { Alert, Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import {
  useGetInstallationQuery, useInstallCertificateMutation
} from '@store/api/installationEndpoints';
import { serverMessage } from '@store/error';
import { useState } from 'react';

const formatDate = (value?: string) => value ? new Date(value).toFormattedDateTimeString() : '-';

// Ids and hashes are one long word each, which a browser will not break on its own: at 390 px the
// fingerprint's 64 characters ran past the card's edge and were cut off. They wrap anywhere instead.
const WRAP_IDS = { overflowWrap: 'anywhere' } as const;

/**
 * Enrolment: what this server asks to be, and what CDMVision certified it as.
 *
 * **Nothing collapses, and there is no stepper.** An earlier version had one, and it was the wrong
 * shape: it implied a sequence you finish, when certificates expire and get renewed and the name
 * and hosts stay editable forever. This is a loop, not a line, so hiding a "completed" stage hides
 * something an administrator wants to see.
 *
 * Two objects, permanently visible, because they have different lifecycles:
 *
 * - **the request**, which is nothing but the two values above it, which is why the download sits
 *   beside Save rather than in a stage of its own
 * - **the certificate**, which arrives days later, expires, and is replaced
 *
 * Licence files are on the Licenses page. They are content rather than setup: one arrives whenever
 * the commercial terms change, and renewing a certificate must not drag them along.
 */
const ActivationPage = () => {
  const { data: installation } = useGetInstallationQuery();
  const [installCertificate, { isLoading: isInstalling }] = useInstallCertificateMutation();

  const [certificateError, setCertificateError] = useState<string>();

  const certificate = installation?.certificate;

  const onInstallCertificate = async (file?: File) => {
    if (!file) return;

    setCertificateError(undefined);

    try {
      await installCertificate(file).unwrap();
    } catch (error) {
      setCertificateError(serverMessage(error) ?? 'The certificate could not be installed.');
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Header
        title='Enrollment'
        description='CDMVision certifies this server to answer as a license server, at named hosts, for a window.'
      />

      {/* Overrides everything below it: the machine changed, so the certificate is no longer in
          force and every licence is suspended until a reissued file arrives. */}
      {installation?.fingerprintMismatch && (
        <Alert severity='error' sx={{ mb: 3 }}>
          This server is running on different hardware than the one it was enrolled for. Licenses
          are suspended. Download a new enrollment request and send it to CDMVision; the file that
          comes back rebinds this installation.
        </Alert>
      )}

      <EnrolmentDetailsCard installation={installation} />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant='h6' gutterBottom>Server certificate</Typography>

          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            What CDMVision sends back. It is re-verified from the stored bytes at every start and
            every hour, so one that expires, or whose hardware fingerprint stops matching, stops
            being in force on its own rather than being trusted until somebody notices.
          </Typography>

          {certificate?.installed ? (
            <>
              <Stack spacing={0.5} sx={{ mb: 2, ...WRAP_IDS }}>
                {/* The name devices show. Worth its own line rather than buried with the ids:
                    it is the value the administrator chose, and the only way to notice that the
                    certificate in force still carries a name they have since changed. */}
                {certificate.serverName && (
                  <Typography variant='body2' color='text.secondary'>
                    Server name: <strong>{certificate.serverName}</strong>
                  </Typography>
                )}
                <Typography variant='body2' color='text.secondary'>
                  Customer: {certificate.customerName} (<code>{certificate.customerId}</code>)
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Permitted hosts: <code>{certificate.permittedHosts?.join(', ')}</code>
                </Typography>
                <Typography variant='body2' color='text.secondary'>
                  Serial {certificate.certSerial} · issued {formatDate(certificate.notBefore)}
                </Typography>
              </Stack>

              {/* Not decoration. An expiry stops every device at once, cached and offline included,
                  so the date a site goes dark belongs where an administrator will see it. From 60
                  days out it takes the colour of the reminder the banner shows on other pages. */}
              <Alert
                severity={certificate.expiryReminderDays === undefined
                  ? 'info'
                  : expiryReminderSeverity(certificate.expiryReminderDays)}
                sx={{ mb: 2 }}
              >
                Valid until <strong>{formatDate(certificate.notAfter)}</strong>
                {certificate.expiryReminderDays !== undefined && certificate.expiresInDays !== undefined &&
                  `, which is ${expiresInText(certificate.expiresInDays)}`}. Every device stops
                on that date, including devices that are offline, so ask CDMVision for a renewal
                before it.
              </Alert>
            </>
          ) : (
            // One that lapsed has stopped every device and is shown as urgently as the banner on
            // the other pages says it; one never installed is a step still to take.
            <Alert severity={certificate?.stored ? 'error' : 'warning'} sx={{ mb: 2 }}>
              {certificate?.problem ?? 'No server certificate has been installed.'}
            </Alert>
          )}

          <FileUpload accept='.suparcert,application/json' onFileChange={onInstallCertificate}>
            <Button
              variant={certificate?.installed ? 'outlined' : 'contained'}
              startIcon={<UploadIcon />}
              disabled={isInstalling}
              component='span'
            >
              {certificate?.installed ? 'Replace certificate' : 'Install certificate'}
            </Button>
          </FileUpload>

          {certificateError && <Alert severity='error' sx={{ mt: 2 }}>{certificateError}</Alert>}
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant='h6' gutterBottom>Installation</Typography>

          <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
            Quote these to CDMVision when asking about this server. The fingerprint is this
            installation on this machine, and it is what a reissued file rebinds to.
          </Typography>

          <Stack spacing={0.5} sx={WRAP_IDS}>
            <Typography variant='body2' color='text.secondary'>
              Installation id: <code>{installation?.installationId ?? '-'}</code>
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Fingerprint: <code>{installation?.fingerprint ?? '-'}</code>
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Server version {installation?.serverVersion ?? '-'} · installed{' '}
              {formatDate(installation?.createdAt)}
            </Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ActivationPage;
